import PageHeader from "@/components/PageHeader";
import { Reveal, Overline } from "@/components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const linkCls = "text-ember font-medium hover:underline";

const groups = [
  {
    title: "Payments",
    items: [
      {
        q: "How do I make a payment on my account?",
        a: (
          <>
            <p className="font-bold text-slate-900 uppercase">
              Please note that all payments must be made on time, no exceptions.
              There is no grace period.
            </p>
            <p className="mt-3">
              Concorde Auto Acceptance reports to Experian and Equifax monthly
              to show other creditors your new credit history. For your
              convenience, we have a number of convenient options for making
              your payments on time:
            </p>
            <p className="mt-4 font-bold text-slate-900">
              AutoPay (ACH) using your bank account or debit or Mastercard (no fees)
            </p>
            <p className="mt-1">
              Setting up automatic debits from your checking or savings account
              has many advantages:
            </p>
            <ol className="list-decimal list-inside mt-2 space-y-1">
              <li><strong className="text-slate-900">No convenience fees</strong> — save up to $500 in convenience fees</li>
              <li><strong className="text-slate-900">Reminder texts</strong> — we'll text you a few days in advance of the debit so you're always ready</li>
              <li><strong className="text-slate-900">Opt-out anytime</strong> — reply STOP to any reminder text to suspend the payment draft</li>
              <li>
                <a href="https://form.jotform.com/222006517397152" className={linkCls} data-testid="faq-autopay-signup">
                  Sign up for AutoPay here
                </a>
              </li>
            </ol>
            <p className="mt-4 font-bold text-slate-900">Using the BlytzPay network (fees apply)</p>
            <ol className="list-decimal list-inside mt-2 space-y-1">
              <li>
                <a href="https://my.blytzpay.com/#/merchant/7acdb786-1f15-4eca-aff5-f4b1fea02516/login" className={linkCls} data-testid="faq-blytzpay-portal">
                  Web portal
                </a>
              </li>
              <li>With cash at hundreds of participating BlytzPay locations — Walmart, 7-Eleven, Walgreens, CVS Pharmacy, and Dollar General</li>
            </ol>
            <p className="mt-4 font-bold text-slate-900">In person at our S.E. Dallas payment center (no fees)</p>
            <p className="mt-1">908 S. Buckner Blvd, Dallas TX 75217 (next to Dallas College Pleasant Grove campus)</p>
            <p className="mt-4 font-bold text-slate-900">With the Wizard app</p>
            <p className="mt-1">Ask your account representative about using the Wizard to manage your Concorde account.</p>
          </>
        ),
      },
      {
        q: "Can I set up automatic recurring payments?",
        a: "Yes. Customers may enroll in automatic recurring payments (AutoPay) from a designated checking or savings account, subject to eligibility. AutoPay helps ensure your payment is made on time each month. You can enroll or manage your automatic payments by logging into your online account or contacting Customer Service.",
      },
      {
        q: "How do I get a payoff quote?",
        a: (
          <>
            You can request a payoff quote by contacting us at{" "}
            <a href="tel:+12142714800" className={linkCls} data-testid="faq-payoff-phone">
              214-271-4800
            </a>
            . Your payoff amount includes your remaining principal balance plus
            any accrued interest and applicable fees through a specific payoff
            date. Because interest accrues daily, payoff amounts are valid only
            through the date provided.
          </>
        ),
      },
      {
        q: "When will I receive my title after paying off my loan?",
        a: "Once your loan has been paid in full and all funds have cleared, Concorde Auto Acceptance will release its lien in accordance with applicable state laws. The timing varies by state, but most customers receive their title or electronic lien release within several weeks after payoff. We recommend you pick up the title in person at our 908 S. Buckner Blvd, Dallas, TX 75217 location to avoid losing the title in the postal system.",
      },
      {
        q: "How is my personal information protected?",
        a: "Protecting your personal information is a top priority. Concorde Auto Acceptance uses industry-standard security measures to safeguard your personal and financial information. We do not sell your personal information, and we only share it as permitted by law and as described in our Privacy Policy. Customers should never share passwords or one-time verification codes with anyone.",
      },
      {
        q: "Does Concorde Auto Acceptance report to credit bureaus?",
        a: "Yes. Concorde Auto Acceptance may report your payment history and account status to both Experian and Equifax credit bureaus. Making your payments on time may positively impact your credit history, while missed or late payments may negatively affect your credit profile.",
      },
    ],
  },
  {
    title: "Insurance",
    items: [
      {
        q: "What insurance coverage is required?",
        a: (
          <>
            <p>
              You may obtain this required insurance from any licensed insurance
              agent and/or authorized insurance company of your choice with the
              following requirements:
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>A maximum deductible of $500</li>
              <li>Policy must name Concorde Auto Acceptance as loss payee, 908 S. Buckner Blvd, Dallas, TX 75217</li>
              <li>Policy must be a "standard policy," with no excluded drivers</li>
              <li>Both buyer and co-buyer must be listed as drivers</li>
              <li>This insurance must be in force during the entire term of your loan agreement</li>
            </ul>
            <p className="mt-4 font-bold text-slate-900">Dealer provided insurance (CPI)</p>
            <p className="mt-1">
              If you do not provide evidence of required insurance, we may
              insure our interest in the financed auto under our Collateral
              Protection Insurance (CPI) policy. The cost of this coverage will
              be charged to your account.
            </p>
            <p className="mt-3 font-bold text-slate-900">Benefits of dealer provided insurance:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>No down payment</li>
              <li>Low premiums</li>
              <li>$500 deductible</li>
              <li>All drivers included</li>
              <li>No DMV check</li>
              <li>No license required</li>
              <li>Immediate coverage</li>
            </ul>
            <p className="mt-3 font-bold text-slate-900 uppercase">
              CPI only protects the value of the vehicle and does not provide
              any type of liability coverage. You must obtain liability
              insurance separately.
            </p>
          </>
        ),
      },
      {
        q: "Where do I send proof of insurance?",
        a: (
          <>
            You may submit proof of insurance either in person at our 908 S.
            Buckner Blvd, Dallas, TX 75217 location or you may email it to{" "}
            <a href="mailto:maribel.colin@concordeaa.com" className={linkCls} data-testid="faq-insurance-email">
              maribel.colin@concordeaa.com
            </a>
            . Please include your account number on all documents to help us
            process your information quickly.
          </>
        ),
      },
      {
        q: "What happens if my insurance lapses?",
        a: "If your required insurance coverage expires or is canceled, you should obtain replacement coverage immediately and provide proof to Concorde Auto Acceptance. Failure to maintain required insurance will result in the addition of single interest insurance being charged on your account which you must pay along with your normal car payment.",
      },
      {
        q: "What is Collateral Protection Insurance (CPI)?",
        a: "If you do not maintain the required insurance coverage, Concorde Auto Acceptance may obtain Collateral Protection Insurance (CPI), also known as force-placed insurance. CPI protects the lender's interest in the vehicle and is not a substitute for your own insurance. It does not provide liability coverage or the same protections as a standard personal auto insurance policy. The cost of CPI will be charged to your loan balance and included in your scheduled payments.",
      },
      {
        q: "What should I do if my vehicle is wrecked?",
        a: (
          <>
            <p>If you have just had an accident, follow these simple steps:</p>
            <ol className="list-decimal list-inside mt-2 space-y-1.5">
              <li>Call 911 if someone appears injured.</li>
              <li>Notify the police. Even if you are at fault, calling the police to file a report may protect you from exaggerated claims by the other party.</li>
              <li>Obtain the driver's license and insurance information from the other parties involved.</li>
              <li>If you have a camera with you, take a picture of the accident scene. If you don't have a camera with you, take pictures of your vehicle as soon as possible.</li>
              <li>Get the accident report number or a copy of the report from the officer on the scene.</li>
              <li>If your vehicle is towed, get the name and telephone number of the tow truck and ask where your vehicle will be taken.</li>
              <li>Obtain a copy of the accident report from the city using the report number obtained earlier.</li>
              <li>Notify ALL insurance companies involved.</li>
              <li>Provide Concorde Auto Acceptance with the pictures and police report.</li>
              <li>Never accept an offer to settle from the insurance company without first discussing your options with your Account Representative.</li>
              <li>Continue to make your payments to preserve your credit rating.</li>
            </ol>
          </>
        ),
      },
    ],
  },
  {
    title: "Financial Hardship",
    items: [
      {
        q: "Can I refinance my loan?",
        a: "Under certain circumstances, Concorde Auto Acceptance can refinance your contract. Please contact your account representative for help.",
      },
      {
        q: "I'm having trouble making my payments. What are my options?",
        a: "If you're facing financial hardship, please contact us as soon as possible. Every situation is unique, and our representatives can review your account to determine whether any assistance options may be available.",
      },
      {
        q: "What should I do if I've lost my job or experienced a financial hardship?",
        a: "Please contact us immediately if you've experienced a job loss, illness, natural disaster, or other financial hardship. We encourage open communication so we can review your account and discuss the options available.",
      },
      {
        q: "What if I am having trouble making my payment?",
        a: "We understand that from time-to-time, unforeseen circumstances could arise that may prevent you from making your payment when due. If you find yourself in this situation, please call before your due date. We are eager to assist you with any reasonable request. Documentation of your unforeseen circumstances may be required for your file. For us to properly assist you, however, we must communicate regularly and honestly with each other.",
      },
      {
        q: "What happens if I miss a payment?",
        a: "If you miss a payment, your account may become past due and late fees may apply as permitted by your loan agreement and applicable law. Continued missed payments may affect your credit and could result in additional collection activity. Contact us immediately if you anticipate missing a payment.",
      },
      {
        q: "When will my account be considered delinquent?",
        a: "An account becomes delinquent when a scheduled payment is not received by its due date. There is no grace period.",
      },
      {
        q: "Can I set up a payment arrangement?",
        a: "Payment extension availability depends on your loan agreement, payment history, account status, and applicable state laws. Contact Customer Service to discuss whether your account may qualify.",
      },
      {
        q: "What happens if my vehicle is repossessed?",
        a: "If your payment is late, your contract may be considered in default and your vehicle may be repossessed. If your vehicle is repossessed, Concorde Auto Acceptance is not obligated to reinstate your contract even if you make up the back payments. If you are anticipating having trouble making a payment on time, please call. You can generally avoid repossession by staying in touch with an account representative. \u201cCommunication is the key to avoiding such unpleasant situations.\u201d",
      },
      {
        q: "How can I reinstate my loan after repossession?",
        a: "If your loan qualifies for reinstatement under your contract and applicable law, you may be required to pay certain past-due amounts, repossession expenses, and other applicable charges. Contact our Collections Department immediately to discuss your available options.",
      },
    ],
  },
  {
    title: "Vehicle Service",
    items: [
      {
        q: "What to do if your vehicle needs servicing",
        a: (
          <>
            <p>If your vehicle needs service our team of technicians are standing by to help.</p>
            <p className="mt-3 font-bold text-slate-900">Location / Making an appointment / Updates:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>7454 C. F. Hawn Frwy, Dallas, TX 75217</li>
              <li>
                <a href="https://goo.gl/maps/2rszH4CoHTFu77KUA" className={linkCls} data-testid="faq-service-map">
                  Click here for map and directions
                </a>
              </li>
              <li>
                Call —{" "}
                <a href="tel:+12143988204" className={linkCls} data-testid="faq-service-phone">
                  (214) 398-8204
                </a>{" "}
                option 4
              </li>
              <li>Text — (214) 225-5196 — text the word UPDATE</li>
            </ul>
            <p className="mt-3">
              All service, except for oil changes, is by appointment only. Use
              the following quick and easy{" "}
              <a href="https://form.jotform.com/210335849884163" className={linkCls} data-testid="faq-service-form">
                appointment form
              </a>
              .
            </p>
          </>
        ),
      },
      {
        q: "What if my vehicle needs to be towed to the service facility?",
        a: (
          <>
            <p className="font-bold text-slate-900">Emergency after-hours tow</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>After 5 PM, Monday – Friday</li>
              <li>After noon on Saturday</li>
              <li>All day Sunday</li>
            </ul>
            <p className="mt-2">
              Call for a tow after hours:{" "}
              <a href="tel:+12143280300" className={linkCls} data-testid="faq-tow-phone">
                (214) 328-0300
              </a>
            </p>
            <p className="mt-3">
              <strong className="text-slate-900">Important:</strong> If this is
              an emergency or if a tow is needed outside the hours of 8am to
              5pm, Monday thru Friday, or 8am to noon on Saturday, please call
              our tow company directly, Big Toe Wrecker Service. Please advise
              them you are an Auto City customer. You will not have to pay for
              the tow at the time of pick up; however, if it is later determined
              that the repairs needed are not covered by your warranty, or if
              the cost of towing exceeds $100, towing fees will be added to your
              bill from the service center, payable upon completion of repairs.
            </p>
            <p className="mt-3 font-bold text-slate-900 uppercase">
              Tow truck drivers do not accept passengers.
            </p>
            <p className="mt-3 font-bold text-slate-900">To request a tow during normal operating hours</p>
            <p className="mt-1">
              Monday – Friday, 8am to 5pm, and Saturday 8am to noon —{" "}
              <a href="https://form.jotform.com/210335849884163" className={linkCls} data-testid="faq-tow-form">
                click here
              </a>
              .
            </p>
          </>
        ),
      },
    ],
  },
];

const Faq = () => (
  <>
    <PageHeader
      testid="faq-header"
      overline="Frequently Asked Questions"
      title="Answers, in"
      accent="plain language."
      description="The questions we hear most from Concorde Auto Acceptance customers. Can't find yours? Our customer service team is one call away."
    />
    <section className="pb-24 lg:pb-32" data-testid="faq-list">
      <div className="mx-auto max-w-3xl px-6 lg:px-10 space-y-16">
        {groups.map((g, gi) => (
          <Reveal key={g.title} delay={0.05}>
            <div data-testid={`faq-group-${gi}`}>
              <Overline>{g.title}</Overline>
              <Accordion type="single" collapsible className="w-full mt-6">
                {g.items.map((f, i) => (
                  <AccordionItem
                    key={i}
                    value={`group-${gi}-item-${i}`}
                    className="border-b border-slate-900/10"
                    data-testid={`faq-item-${gi}-${i}`}
                  >
                    <AccordionTrigger
                      data-testid={`faq-trigger-${gi}-${i}`}
                      className="text-left font-display font-bold text-lg lg:text-xl text-slate-900 hover:text-ember hover:no-underline py-7 [&[data-state=open]>svg]:text-ember"
                    >
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent
                      data-testid={`faq-content-${gi}-${i}`}
                      className="text-slate-600 leading-relaxed pb-7"
                    >
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  </>
);

export default Faq;
